import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input, InputPassword } from "@/components/ui/input";
import { useAddSendGridSmtp } from "@/hooks/use-mailbox";
import { extractErrorMessage } from "@/lib/utils";
import { SendGridSmtpPayload } from "@/types/mailbox.types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import * as z from "zod";

const formSchema = z.object({
  apiKey: z.string().trim().min(1, "API key is required"),
  email: z.string().trim().min(1, "Email is required"),
});

type FormData = z.infer<typeof formSchema>;

interface SendGridWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const SendGridWizard = ({ open, onOpenChange }: SendGridWizardProps) => {
  const { mutateAsync: addSendGridSmtp, isPending } = useAddSendGridSmtp();

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      apiKey: "",
      email: "",
    },
  });

  const onSubmit = async (values: FormData) => {
    try {
      const payload: SendGridSmtpPayload = {
        ...values,
        smtpHost: "smtp.sendgrid.net",
        smtpUsername: "apikey",
        smtpPassword: values.apiKey,
        smtpPort: 587,
        encryptionMethod: "TLS",
      };

      await addSendGridSmtp(payload);
      toast.success(
        "SendGrid SMTP settings have been successfully configured."
      );
      onOpenChange(false);
      form.reset();
    } catch (error) {
      const message = extractErrorMessage(
        error,
        "Failed to configure SendGrid SMTP settings."
      );
      toast.error(message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>SendGrid SMTP Configuration</DialogTitle>
          <DialogDescription>
            You are connected to Sendgrid using the SMTP below
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-foreground font-medium">
                      Email <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter your SendGrid email"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="apiKey"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-foreground font-medium">
                      API key <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <InputPassword
                        placeholder="Enter your SendGrid API key"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                    <Alert variant="info">
                      <AlertDescription>
                        <span className="font-medium">Note:</span> We
                        prioritize your privacy. All SMTP details are securely
                        stored and fully encrypted to ensure your information
                        is protected at all times.
                      </AlertDescription>
                    </Alert>
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter>
              <Button
                type="submit"
                disabled={isPending}
              >
                {isPending ? "Saving..." : "Save Configuration"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
